// src/pages/AdminAseguradoras.js
// =========================================================================
// ASEGURADORAS (solo admin)
//
// Compañías con las que la clínica tiene concierto, sus datos para facturarles
// y su baremo: por cada servicio del tarifario que cubren, lo que paga la
// aseguradora, el copago del paciente y si hace falta autorización previa.
// Un servicio que no está marcado no lo cubre esa aseguradora.
// =========================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { euros } from '../utils/formatos';

const entrada = 'w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-800 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15';
const etiqueta = 'block mb-1 text-xs font-bold text-slate-600';

const CAMPOS = [
    ['razonSocial', 'Razón social', 'Para la factura mensual'],
    ['cif', 'CIF', 'A12345678'],
    ['direccion', 'Dirección de facturación', ''],
    ['email', 'Correo de facturación', ''],
    ['telefono', 'Teléfono', '']
];

// Copia editable: el baremo como filas por servicio del tarifario
const aEdicion = (a, servicios) => ({
    ...a,
    filas: servicios.map(s => {
        const linea = a.baremo.find(l => l.servicio === s.codigo.toUpperCase());
        return {
            servicio: s.codigo.toUpperCase(),
            nombre: s.nombre,
            precio: s.precio,
            cubre: Boolean(linea),
            importe: linea ? String(linea.importe) : '',
            copago: linea ? String(linea.copago) : '0',
            requiereAutorizacion: linea ? linea.requiereAutorizacion : false
        };
    })
});

export default function AdminAseguradoras() {
    const { showToast } = useToast();
    const toastRef = useRef(showToast);
    toastRef.current = showToast;

    const [aseguradoras, setAseguradoras] = useState([]);
    const [habituales, setHabituales] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [seleccion, setSeleccion] = useState(null); // id
    const [edicion, setEdicion] = useState(null);
    const [nombreNueva, setNombreNueva] = useState('');
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [errores, setErrores] = useState({});

    const cargar = useCallback(async () => {
        const [lista, sugeridas, config] = await Promise.all([
            api.get('/seguros/aseguradoras?todas=1'),
            api.get('/seguros/aseguradoras/habituales'),
            api.get('/clinica/configuracion')
        ]);
        setAseguradoras(lista);
        setHabituales(sugeridas);
        setServicios((config.servicios || []).filter(s => s.activo));
        return lista;
    }, []);

    useEffect(() => {
        cargar()
            .then(lista => { if (lista.length) setSeleccion(lista[0].id); })
            .catch(err => toastRef.current(err.message || 'No se pudieron cargar las aseguradoras.', 'error'))
            .finally(() => setCargando(false));
    }, [cargar]);

    const actual = aseguradoras.find(a => a.id === seleccion) || null;
    useEffect(() => {
        setEdicion(actual ? aEdicion(actual, servicios) : null);
        setErrores({});
    }, [actual, servicios]);

    const libres = useMemo(() => {
        const clave = (t) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const usadas = new Set(aseguradoras.map(a => clave(a.nombre)));
        return habituales.filter(n => !usadas.has(clave(n)));
    }, [aseguradoras, habituales]);

    const anadir = async (nombre) => {
        const limpio = String(nombre || '').trim();
        if (limpio.length < 2) return;
        try {
            const nueva = await api.post('/seguros/aseguradoras', { nombre: limpio });
            await cargar();
            setSeleccion(nueva.id);
            setNombreNueva('');
            showToast(`${nueva.nombre} añadida. Indica qué servicios cubre.`, 'success');
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    const cambiar = (campo, valor) => setEdicion(e => ({ ...e, [campo]: valor }));
    const cambiarFila = (i, campo, valor) =>
        setEdicion(e => ({ ...e, filas: e.filas.map((f, j) => (j === i ? { ...f, [campo]: valor } : f)) }));

    const guardar = async () => {
        const nuevosErrores = {};
        const baremo = [];
        for (const f of edicion.filas.filter(x => x.cubre)) {
            const importe = Number(String(f.importe).replace(',', '.'));
            const copago = Number(String(f.copago || 0).replace(',', '.'));
            if (!(importe >= 0) || f.importe === '') nuevosErrores[f.servicio] = 'Indica lo que paga la aseguradora';
            else if (!(copago >= 0)) nuevosErrores[f.servicio] = 'Copago no válido';
            baremo.push({ servicio: f.servicio, importe, copago, requiereAutorizacion: f.requiereAutorizacion });
        }
        setErrores(nuevosErrores);
        if (Object.keys(nuevosErrores).length) return;

        setGuardando(true);
        try {
            const datos = Object.fromEntries(CAMPOS.map(([c]) => [c, edicion[c] || '']));
            await api.put(`/seguros/aseguradoras/${edicion.id}`, { ...datos, nombre: edicion.nombre, activa: edicion.activa, baremo });
            await cargar();
            showToast('Aseguradora guardada.', 'success');
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setGuardando(false);
        }
    };

    if (cargando) {
        return (
            <div className="p-6 md:p-8 max-w-6xl mx-auto">
                <div className="h-8 w-64 bg-slate-200 rounded-lg animate-pulse mb-6"></div>
                <div className="h-96 bg-white border border-slate-200 rounded-[2rem] animate-pulse"></div>
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto animate-fade-in">
            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight">Aseguradoras</h1>
                <p className="text-sm font-semibold text-slate-500 mt-1">
                    Compañías con las que trabaja la clínica y lo que paga cada una por vuestros servicios. Al dar una cita por seguro se aplica este baremo.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 items-start">
                {/* LISTA */}
                <aside className="bg-white border border-slate-200 rounded-[2rem] p-4 shadow-sm space-y-4">
                    {aseguradoras.length === 0 ? (
                        <p className="px-2 text-sm font-semibold text-slate-400">Todavía no hay ninguna. Añade las que concierta la clínica.</p>
                    ) : (
                        <ul className="space-y-1">
                            {aseguradoras.map(a => (
                                <li key={a.id}>
                                    <button type="button" onClick={() => setSeleccion(a.id)}
                                            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-left text-sm font-bold transition-colors ${a.id === seleccion ? 'bg-clinica-primary text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
                                        <span className="truncate">{a.nombre}</span>
                                        <span className={`shrink-0 text-[10px] font-black uppercase tracking-widest ${a.id === seleccion ? 'text-white/80' : a.activa ? 'text-slate-400' : 'text-rose-500'}`}>
                                            {a.activa ? `${a.baremo.length} serv.` : 'Inactiva'}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="pt-3 border-t border-slate-100 space-y-3">
                        <form onSubmit={e => { e.preventDefault(); anadir(nombreNueva); }} className="flex gap-2">
                            <input value={nombreNueva} onChange={e => setNombreNueva(e.target.value)} maxLength={80}
                                   placeholder="Otra aseguradora" aria-label="Nombre de la aseguradora" className={entrada} />
                            <button type="submit" disabled={nombreNueva.trim().length < 2}
                                    className="shrink-0 px-3 rounded-lg text-xs font-black text-white bg-clinica-primary disabled:opacity-40">Añadir</button>
                        </form>
                        {libres.length > 0 && (
                            <div>
                                <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">Habituales</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {libres.map(n => (
                                        <button key={n} type="button" onClick={() => anadir(n)}
                                                className="px-2.5 py-1 rounded-lg text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200">
                                            + {n}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </aside>

                {/* EDICIÓN */}
                {edicion ? (
                    <section className="bg-white border border-slate-200 rounded-[2rem] p-6 md:p-8 shadow-sm space-y-8">
                        <div className="flex flex-wrap items-end justify-between gap-4">
                            <div className="flex-1 min-w-[220px]">
                                <label htmlFor="aseg-nombre" className={etiqueta}>Nombre</label>
                                <input id="aseg-nombre" value={edicion.nombre} onChange={e => cambiar('nombre', e.target.value)} maxLength={80} className={entrada} />
                            </div>
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                                <input type="checkbox" checked={edicion.activa} onChange={e => cambiar('activa', e.target.checked)} className="w-4 h-4 accent-clinica-primary" />
                                La clínica trabaja con ella
                            </label>
                        </div>

                        <div>
                            <h2 className="text-lg font-black text-slate-900">Datos de facturación</h2>
                            <p className="text-xs font-semibold text-slate-500 mt-1 mb-4">Los que aparecerán en la factura mensual que se le envía.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {CAMPOS.map(([campo, texto, ayuda]) => (
                                    <div key={campo} className={campo === 'direccion' ? 'sm:col-span-2' : ''}>
                                        <label htmlFor={`aseg-${campo}`} className={etiqueta}>{texto}</label>
                                        <input id={`aseg-${campo}`} value={edicion[campo] || ''} onChange={e => cambiar(campo, e.target.value)}
                                               placeholder={ayuda} className={entrada} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h2 className="text-lg font-black text-slate-900">Baremo</h2>
                            <p className="text-xs font-semibold text-slate-500 mt-1 mb-4">
                                Marca los servicios que cubre. «Paga» es lo que abona la aseguradora; el copago lo paga el paciente en la clínica.
                            </p>
                            {servicios.length === 0 ? (
                                <p className="text-sm font-semibold text-slate-400">No hay servicios activos en el tarifario.</p>
                            ) : (
                                <div className="overflow-x-auto border border-slate-100 rounded-2xl">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                            <tr>
                                                <th className="p-3 text-left">Servicio</th>
                                                <th className="p-3 text-right">Privado</th>
                                                <th className="p-3 text-left w-28">Paga</th>
                                                <th className="p-3 text-left w-28">Copago</th>
                                                <th className="p-3 text-center">Autorización</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {edicion.filas.map((f, i) => (
                                                <tr key={f.servicio} className={f.cubre ? '' : 'text-slate-400'}>
                                                    <td className="p-3">
                                                        <label className="flex items-center gap-2 font-bold">
                                                            <input type="checkbox" checked={f.cubre} onChange={e => cambiarFila(i, 'cubre', e.target.checked)}
                                                                   className="w-4 h-4 accent-clinica-primary" />
                                                            <span>{f.nombre}</span>
                                                        </label>
                                                        {errores[f.servicio] && <div role="alert" className="mt-1 ml-6 text-xs font-semibold text-rose-600">{errores[f.servicio]}</div>}
                                                    </td>
                                                    <td className="p-3 text-right font-semibold">{euros(f.precio)}</td>
                                                    <td className="p-3">
                                                        <input type="number" min="0" step="0.01" value={f.importe} disabled={!f.cubre}
                                                               onChange={e => cambiarFila(i, 'importe', e.target.value)}
                                                               aria-label={`Lo que paga la aseguradora por ${f.nombre}`} className={`${entrada} disabled:opacity-40`} />
                                                    </td>
                                                    <td className="p-3">
                                                        <input type="number" min="0" step="0.01" value={f.copago} disabled={!f.cubre}
                                                               onChange={e => cambiarFila(i, 'copago', e.target.value)}
                                                               aria-label={`Copago de ${f.nombre}`} className={`${entrada} disabled:opacity-40`} />
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        <input type="checkbox" checked={f.requiereAutorizacion} disabled={!f.cubre}
                                                               onChange={e => cambiarFila(i, 'requiereAutorizacion', e.target.checked)}
                                                               aria-label={`${f.nombre} requiere autorización`} className="w-4 h-4 accent-clinica-primary disabled:opacity-40" />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end">
                            <button type="button" onClick={guardar} disabled={guardando}
                                    className="px-6 py-3 rounded-xl text-sm font-black text-white bg-clinica-primary hover:bg-blue-700 disabled:opacity-50">
                                {guardando ? 'Guardando…' : 'Guardar aseguradora'}
                            </button>
                        </div>
                    </section>
                ) : (
                    <section className="bg-white border border-dashed border-slate-200 rounded-[2rem] p-10 text-center text-sm font-semibold text-slate-400">
                        Añade una aseguradora para configurar su baremo.
                    </section>
                )}
            </div>
        </div>
    );
}
